/** @type {Promise<string> | null} */
let sizeChartContentPromise = null;

class SizeCharts extends HTMLElement {
  constructor() {
    super();
    this.dynamicRender = this.hasAttribute('data-dynamic-source');
    this._contentLoaded = false;
    this.viewModel = this.getViewModel();

    if (this.dynamicRender) {
      this.patchModalOpen();
    } else {
      this.bindEvents(this);
    }
  }

  bindEvents(target) {
    this.viewModel = this.getViewModel();
    this.parseAttributes(target);
    this.hideSizeCharts();
    this.hideIrrelevantHeadings();
    this.bindTabHandlers();
    this.showRelevantSizeChart();
    this.setTitle();
  }

  setTitle() {
    const { $modalTitle } = this.viewModel;

    if (this.modalTitle && $modalTitle) {
      $modalTitle.textContent = this.modalTitle;
    }
  }

  patchModalOpen() {
    const { $modal } = this.viewModel;
    if (!$modal || typeof $modal.open !== 'function') return;

    if (this._originalModalOpen) return;

    this._originalModalOpen = $modal.open.bind($modal);

    $modal.open = async (...args) => {
      const target = args[0];
      this._originalModalOpen(...args);

      try {
        if (!this._contentLoaded) {
          this.setLoadingState(true);
        }

        await this.ensureContentLoaded();
        this.setLoadingState(false);
        this.bindEvents(target);
      } catch {
        this.setErrorState();
      }
    };
  }

  async ensureContentLoaded() {
    if (this._contentLoaded) return;

    const html = await fetchSizeChartContent(this.getAttribute('data-section-id') || 'size-chart-modal-content');
    this.injectContent(html);
    this._contentLoaded = true;
    this.setAttribute('aria-busy', 'false');
  }

  injectContent(html) {
    this.innerHTML = html;
  }

  setLoadingState(isLoading) {
    const loadingEl = this.querySelector('.size-charts__loading');
    if (loadingEl instanceof HTMLElement) {
      loadingEl.classList.toggle('visually-hidden', !isLoading);
      loadingEl.setAttribute('aria-live', isLoading ? 'polite' : 'off');
    }
  }

  setErrorState() {
    this.setAttribute('aria-busy', 'false');
    this.innerHTML = '<p class="size-charts__error fixed-body-m">Unable to load size chart. Please try again.</p>';
  }

  getViewModel() {
    const $modal = this.closest('modal-slideout');

    return {
      $ringsHeading: this.querySelector('#rings-heading-section'),
      $adultKidsHeading: this.querySelector('#adult-kids-heading-section'),
      $tabs: this.querySelectorAll('.tab'),
      $tabContents: this.querySelectorAll('.tab-content'),
      $modal,
      $modalTitle: $modal?.querySelector('.modal__header-title') ?? null,
      $sizeCharts: this.querySelectorAll('[id*="SizeChart"]'),
    };
  }

  parseAttributes(target) {
    this.sizeGuide = target.getAttribute('data-size-guide')?.toLowerCase() || 'adult'; // "Expect values: Adult, Kids"
    this.gender = target.getAttribute('data-gender')?.toLowerCase() || ''; // "Expect values: Mens, Womens, Unisex, Boys, Girls"
    this.modalTitle = target.getAttribute('data-size-chart-title') || '';
    this.kidsAge = target.getAttribute('data-kid-age')?.toLowerCase() || ''; // "Expect values: Big, Little, Infant"
    this.sizeChartType = target.getAttribute('data-size-chart-type')?.toLowerCase() || 'footwear'; // "Expect values: Footwear, Rings, Tops, Bottons, Belts, Hats"
    this.genderValue = this.gender === 'unisex' || this.gender === '' ? 'womens' : this.gender;
  }

  hideIrrelevantHeadings() {
    const { $ringsHeading, $adultKidsHeading } = this.viewModel;

    const hideRings = !['rings'].includes(this.sizeChartType);
    const hideAdultKids = ['rings', 'tops', 'bottoms', 'hats', 'belts'].includes(this.sizeChartType);

    if ($ringsHeading) $ringsHeading.style.display = hideRings ? 'none' : 'block';
    if ($adultKidsHeading) $adultKidsHeading.style.display = hideAdultKids ? 'none' : 'block';
  }

  // Initial state for all charts
  hideSizeCharts() {
    this.viewModel.$sizeCharts?.forEach((el) => {
      el.style.display = 'none';
    });
  }

  showRelevantSizeChart() {
    const show = (id) => {
      const el = this.querySelector(id);
      if (el instanceof HTMLElement) el.style.display = 'block';
    };

    if (['rings', 'hats', 'belts'].includes(this.sizeChartType)) {
      show(`#${this.sizeChartType}SizeChart`);
      this.activateTab(`${this.genderValue}-${this.sizeChartType}`);
    } else if (['tops', 'bottoms'].includes(this.sizeChartType)) {
      show(`#${this.sizeChartType}SizeChart`);
    } else if (this.sizeGuide === 'kids') {
      show(`#${this.sizeGuide}SizeChart`);
      this.activateTab(this.kidsAge);
    } else {
      show(`#${this.sizeGuide}SizeChart`);
      this.activateTab(this.genderValue);
    }
  }

  activateTab(id) {
    const tab = this.querySelector(`#${id}`);

    if (tab instanceof HTMLElement) {
      tab.click();
    }
  }

  bindTabHandlers() {
    if (this._tabsBound) return;
    this._tabsBound = true;

    const { $tabs, $tabContents } = this.viewModel;

    $tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        $tabs.forEach((item) => {
          item.classList.remove('tab-active');
          item.firstElementChild?.classList.replace('fixed-utility-m-em', 'fixed-utility-m');
        });

        $tabContents.forEach((content) => content.classList.remove('active'));

        tab.classList.add('tab-active');
        tab.firstElementChild?.classList.replace('fixed-utility-m', 'fixed-utility-m-em');

        const content = this.querySelector(`#tab-content-${tab.id}`);
        if (content instanceof HTMLElement) {
          content.classList.add('active');
        }
      });
    });
  }
}

/**
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
async function fetchSizeChartContent(sectionId) {
  if (!sizeChartContentPromise) {
    sizeChartContentPromise = loadSizeChartSection(sectionId);
  }

  return sizeChartContentPromise;
}

/**
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
async function loadSizeChartSection(sectionId) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}?section_id=${sectionId}`);

  if (!response.ok) {
    throw new Error(`Failed to load size chart content (${response.status})`);
  }

  const html = await response.text();
  const innerHtml = getSectionInnerHTML(html, '.shopify-section');

  if (!innerHtml) {
    throw new Error('Size chart section returned empty content');
  }

  return innerHtml;
}

/**
 * @param {string} html
 * @param {string} selector
 * @returns {string | undefined}
 */
function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
}

customElements.define('size-charts', SizeCharts);
